import React, {
  useContext, useEffect, useState,
} from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import parsePhoneNumber from 'libphonenumber-js/min';
import { useParams } from 'react-router';
import CustomPhoneInput from '../../../../../../generic/CustomPhoneInput';
import CustomInput from '../../../../../../generic/CustomInput';
import CustomCheckbox from '../../../../../../generic/CustomCheckbox';
import CustomTextarea from '../../../../../../generic/CustomTextarea';
import Disclaimer from '../Disclaimer/Disclaimer';
import './ContactInfo.scss';
import UploaderSection from './UploaderSection';
import getCarbonFilesList from '../../../../../../services/getCarbonFilesList';
import { appStore } from '../../../../../../state/app';

const ContactInfo = ({ defaultValues, tabKey, isActiveTab, disabled }) => {
  const translate = useIntl();
  const { register, errors, setError, clearErrors, control } = useFormContext();
  const { [tabKey]: formErrors } = errors;

  const { update, state: { app } } = useContext(appStore);
  const { reloadFilesFlag } = app;
  const [isVisibleDisclaimer, setIsVisibleDisclaimer] = useState(false);
  const [filesListTenure, setFilesListTenure] = useState([]);
  const [filesListOthers, setFilesListOthers] = useState([]);
  const { nameId } = useParams();
  const closeDisclaimer = () => setIsVisibleDisclaimer(false);
  const openDisclaimer = (e) => {
    e.preventDefault();
    setIsVisibleDisclaimer(true);
  };

  const isValidPhoneNumber = (number) => {
    const parsedNumber = parsePhoneNumber(`+${number}` || '');
    if (parsedNumber) return parsedNumber.isPossible();
    return false;
  };
  useEffect(() => {
    update('app.filesGen', filesListTenure.filter((el) => el?.name));
  }, [filesListTenure]);
  useEffect(() => {
    update('app.filesOther', filesListOthers.filter((el) => el?.name));
  }, [filesListOthers]);
  useEffect(async () => {
    const list = await getCarbonFilesList(nameId) ?? [];
    if (list.length) {
      const genList = [];
      list.forEach((el) => {
        if (el?.file_type === 'general') genList.push({ path: el.filename, id: el.id });
      });
      const otherList = [];
      list.forEach((el) => {
        if (el?.file_type === 'other') otherList.push({ path: el.filename, id: el.id });
      });

      if (genList.length) setFilesListTenure(genList);
      if (otherList.length)setFilesListOthers(otherList);
    }
  }, [reloadFilesFlag]);
  return (
    <div className="contact-info__wrapper" style={{ display: isActiveTab ? 'block' : 'none' }}>
      {isVisibleDisclaimer && <Disclaimer closeHandler={closeDisclaimer} />}
      <div className="contact-info__title-gen">{translate.formatMessage({ id: 'carbon.form.documents' })}</div>
      <div className="contact-info">
        <UploaderSection
          filesListOthers={filesListOthers}
          setFilesListOthers={setFilesListOthers}
          filesListTenure={filesListTenure}
          setFilesListTenure={(e) => {
            setFilesListTenure(e);
            if (e.length) {
              return clearErrors('contactInfo.documentationComment');
            }
            setError('contactInfo.documentationComment', { type:
                'required' });
          }}
        >
          <div>
            <p className="contact-info__field-title">{translate.formatMessage({ id: 'previousReports.filedCommentLabel' })}</p>
            <CustomTextarea
              label={translate.formatMessage({ id: 'previousReports.filedCommentLabel.placeholder' })}
              classTextarea="textarea-field"
              customClassLabel="contact-info__field-label"
              name={`${tabKey}.documentationComment`}
              value={defaultValues.documentationComment}
              register={register({ required: !filesListTenure.length, maxLength: 1000 })}
              error={formErrors?.documentationComment}
              disabled={disabled}
            />
          </div>
        </UploaderSection>

        <div className="contact-info__item">
          <div className="contact-info__title-gen">{translate.formatMessage({ id: 'carbon.form.contacts' })}</div>
          <p className="form-field-title">{translate.formatMessage({ id: 'carbon.form.projectManagement' })}</p>
          <div className="contact-info__group">
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.organisation' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.organisation' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.projectManagement.organisation`}
              value={defaultValues.projectManagement.organisation}
              register={register({ required: true })}
              error={formErrors?.projectManagement?.organisation}
              disabled={disabled}
            />
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.email' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.email' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.projectManagement.email`}
              value={defaultValues.projectManagement.email}
              register={register({
                required: true,
                // eslint-disable-next-line no-useless-escape
                pattern: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
              })}
              error={formErrors?.projectManagement?.email}
              disabled={disabled}
            />
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.personName' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.personName' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.projectManagement.personName`}
              value={defaultValues.projectManagement.personName}
              register={register({ required: true })}
              error={formErrors?.projectManagement?.personName}
              disabled={disabled}
            />
            <CustomPhoneInput
              label={translate.formatMessage({ id: 'carbon.form.telephone' })}
              name={`${tabKey}.projectManagement.phoneNumber`}
              defaultValue={defaultValues.projectManagement.phoneNumber}
              control={control}
              rules={{
                required: true,
                validate: {
                  validPhoneNumber: (value) => isValidPhoneNumber(value),
                },
              }}
              error={formErrors?.projectManagement?.phoneNumber}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="contact-info__item">
          <p className="form-field-title">{translate.formatMessage({ id: 'carbon.form.treePlanting' })}</p>
          <div className="contact-info__group">
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.organisation' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.organisation' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.treePlanting.organisation`}
              value={defaultValues.treePlanting.organisation}
              register={register({ required: true })}
              error={formErrors?.treePlanting?.organisation}
              disabled={disabled}
            />
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.email' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.email' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.treePlanting.email`}
              value={defaultValues.treePlanting.email}
              register={register({
                required: true,
                // eslint-disable-next-line no-useless-escape
                pattern: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
              })}
              error={formErrors?.treePlanting?.email}
              disabled={disabled}
            />
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.personName' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.personName' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.treePlanting.personName`}
              value={defaultValues.treePlanting.personName}
              register={register({ required: true })}
              error={formErrors?.treePlanting?.personName}
              disabled={disabled}
            />
            <CustomPhoneInput
              label={translate.formatMessage({ id: 'carbon.form.telephone' })}
              name={`${tabKey}.treePlanting.phoneNumber`}
              defaultValue={defaultValues.treePlanting.phoneNumber}
              control={control}
              rules={{
                required: true,
                validate: {
                  validPhoneNumber: (value) => isValidPhoneNumber(value),
                },
              }}
              error={formErrors?.treePlanting?.phoneNumber}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="contact-info__item">
          <p className="form-field-title">{translate.formatMessage({ id: 'carbon.form.forestMonitoring' })}</p>
          <div className="contact-info__group">
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.organisation' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.organisation' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.forestMonitoring.organisation`}
              value={defaultValues.forestMonitoring.organisation}
              register={register({ required: true })}
              error={formErrors?.forestMonitoring?.organisation}
              disabled={disabled}
            />
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.email' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.email' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.forestMonitoring.email`}
              value={defaultValues.forestMonitoring.email}
              register={register({
                required: true,
                // eslint-disable-next-line no-useless-escape
                pattern: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
              })}
              error={formErrors?.forestMonitoring?.email}
              disabled={disabled}
            />
            <CustomInput
              type="text"
              label={translate.formatMessage({ id: 'carbon.form.personName' })}
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.personName' })}
              customClass="input-field"
              customClassLabel="form-field-title-secondary"
              name={`${tabKey}.forestMonitoring.personName`}
              value={defaultValues.forestMonitoring.personName}
              register={register({ required: true })}
              error={formErrors?.forestMonitoring?.personName}
              disabled={disabled}
            />
            <CustomPhoneInput
              label={translate.formatMessage({ id: 'carbon.form.telephone' })}
              name={`${tabKey}.forestMonitoring.phoneNumber`}
              defaultValue={defaultValues.forestMonitoring.phoneNumber}
              control={control}
              rules={{
                required: true,
                validate: {
                  validPhoneNumber: (value) => isValidPhoneNumber(value),
                },
              }}
              error={formErrors?.forestMonitoring?.phoneNumber}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="contact-info__item">
          <div className="comments__wrapper-textarea">
            <p className="comments__title">{translate.formatMessage({ id: 'carbon.form.comments' })}</p>
            <div className="comments__wrapper-textarea">
              <CustomTextarea
                label={translate.formatMessage({ id: 'carbon.form.additionalComments' })}
                placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
                classTextarea="textarea-field"
                customClassLabel="form-field-title"
                name={`${tabKey}.comments`}
                value={defaultValues.comments}
                register={register({ required: false, maxLength: 1000 })}
                error={formErrors?.comments}
                disabled={disabled}
              />
            </div>
            <ul className="comments__confirm-list" aria-label={translate.formatMessage({ id: 'carbon.form.byClickingSubmit' })}>
              <li>{translate.formatMessage({ id: 'carbon.form.allStatutoryOrCustomaryRights' })}</li>
              <li>{translate.formatMessage({ id: 'carbon.form.humanRightsAndFreedoms' })}</li>
              <li>{translate.formatMessage({ id: 'carbon.form.projectComplies' })}</li>
              <li>{translate.formatMessage({ id: 'carbon.form.notRegisteredInOtherGHG' })}</li>
              <li>{translate.formatMessage({ id: 'carbon.form.allowedByNationalLegislation' })}</li>
              <li>{translate.formatMessage({ id: 'carbon.form.herebyCertify' })}</li>
            </ul>
            <CustomCheckbox
              type="checkbox"
              title={(
                <span className="comments__disclaimer">
                  <FormattedMessage
                    id="carbon.form.disclaimer"
                    values={{ u: (chunks) => <u onClick={openDisclaimer}>{chunks}</u> }}
                  />
                </span>
                )}
              customClass="radio-button-wrapper"
              name={`${tabKey}.disclaimer`}
              defaultChecked={defaultValues.disclaimer}
              register={register({ required: true })}
              error={formErrors?.disclaimer}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInfo;
